import { motion } from "framer-motion";
import { Monitor, Mail, PenTool, Camera } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Button from "@/components/Button";
import { SERVICES } from "@/data/site";
import { scaleImg, easeLux } from "@/lib/motion";
import Seo from "@/components/Seo";

const ICONS = { Monitor, Mail, PenTool, Camera };

const Servicios = () => {
  return (
    <div data-testid="servicios-page">
      <Seo
        title="Servicios"
        description="Diseño web, invitaciones digitales, diseño gráfico y fotografía profesional. Cuatro formas de dar identidad y alma a tu marca con Lu Design."
        path="/servicios"
      />
      <PageHero
        overline="Servicios"
        titleLines={["Diseño con", "propósito y alma."]}
        intro="Cada proyecto es una historia distinta. Estas son las cuatro formas en que ayudamos a las marcas a verse, sentirse y conectar mejor."
        testId="servicios-hero"
      />

      <section className="relative z-10 bg-ivory pb-8">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const reversed = i % 2 === 1;
            return (
              <div
                key={s.id}
                data-testid={`service-block-${s.id}`}
                className="grid items-center gap-10 md:gap-16 md:grid-cols-2 border-t border-rosegold/30 py-16 md:py-24"
              >
                {/* Imagen */}
                <div className={`relative ${reversed ? "md:order-2" : ""}`}>
                  <div className="relative overflow-hidden">
                    <motion.img
                      variants={scaleImg}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, amount: 0.3 }}
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      decoding="async"
                      className="h-[26rem] w-full object-cover md:h-[32rem]"
                    />
                  </div>
                  <div
                    className={`absolute -bottom-4 h-24 w-24 -z-0 bg-powder/60 ${
                      reversed ? "-right-4" : "-left-4"
                    }`}
                  />
                </div>

                {/* Texto */}
                <div className={reversed ? "md:order-1" : ""}>
                  <Reveal>
                    <div className="flex items-center gap-4">
                      <span className="font-serif text-3xl text-rosegold">{s.number}</span>
                      <Icon className="h-8 w-8 text-plum" strokeWidth={1} />
                    </div>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <h2 className="mt-6 font-serif font-light text-charcoal text-4xl md:text-5xl tracking-tight">
                      {s.title}
                    </h2>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <p className="mt-6 max-w-md font-sans font-light text-base md:text-lg leading-relaxed text-charcoal/70">
                      {s.long}
                    </p>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-serif font-light text-charcoal text-4xl md:text-5xl tracking-tight leading-tight">
              ¿Tu proyecto encaja en alguno de estos?{" "}
              <span className="italic text-plum">Conversemos.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8, ease: easeLux }}
              className="mt-10 flex justify-center"
            >
              <Button to="/contacto" testId="servicios-cta">
                Solicitar cotización
              </Button>
            </motion.div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Servicios;
